import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';

import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import ClockIcon from '@atlaskit/icon/core/clock';
import type {
	NavItemConfig,
	ObjectListFlyoutAction,
	ObjectListFlyoutItemConfig,
} from '@atlassian/navigation-common/types';

import type { ForYouConfig } from '../../../common/forYouTypes';
import { omitSafely } from '../../../common/utils/omit-safely';

import { RECENT_TEST_ID } from './constants';
import { messages } from './messages';

const elemBefore = <ClockIcon label="" spacing="spacious" />;

export function useRecentNavItem(forYou: ForYouConfig): [string, NavItemConfig][] {
	const { formatMessage } = useIntl(); // formatMessage() is not a stable function
	const recentConfig = forYou?.recent;
	const viewAllRecentItemsLabel = formatMessage(messages.viewAllRecentItemsLabel);
	const label = formatMessage(messages.label);
	const searchPlaceholder = formatMessage(messages.searchPlaceholder);

	return useMemo(() => {
		if (!recentConfig) {
			return [];
		} else {
			// This is where we map the forYou config to the ObjectListFlyout config.
			const actions: ObjectListFlyoutAction[] | undefined = recentConfig.viewAllUrl
				? [
						{
							elemBefore: <AlignTextLeftIcon label={''} />,
							label: viewAllRecentItemsLabel,
							href: recentConfig.viewAllUrl,
							onClick: recentConfig.viewAllOnClick,
						},
					]
				: recentConfig.viewAllOnClick && [
						{
							elemBefore: <AlignTextLeftIcon label={''} />,
							label: viewAllRecentItemsLabel,
							onClick: recentConfig.viewAllOnClick,
						},
					];

			// Omit the type-props from FlyoutProps that are not used by ObjectListFlyout
			const safeConfig = omitSafely(recentConfig, 'menuItemId', 'viewAllOnClick', 'viewAllUrl');

			const value: ObjectListFlyoutItemConfig = {
				...safeConfig,
				actions,
				elemBefore,
				label,
				searchPlaceholder,
				testId: RECENT_TEST_ID,
				type: 'object-list-flyout',
			};

			return [[recentConfig.menuItemId ?? 'global.sidebar.recent', value]];
		}
	}, [label, searchPlaceholder, viewAllRecentItemsLabel, recentConfig]);
}
